/**
 * Sim Mode scenario fixtures — synthetic session states for previewing any booth
 * page without walking the linear flow. All media is generated on a canvas at
 * runtime (data URLs), so fixtures work offline and never touch the network.
 */

import type { SessionStateData } from '@renderer/types/sessionState'
import type { ConfigState, FrameOption, Filter, Package } from '@renderer/types/configState'

export interface ScenarioContext {
  projectId: string
  userId: string
  config: ConfigState
  frames: FrameOption[]
  filters: Filter[]
}

export interface ScenarioOptions {
  /** Seconds until the payment QR expires (payment scenarios). */
  expiresIn?: number
  /**
   * Override the number of captured placeholder photos (capture-and-later
   * scenarios). Default derives from the project's first frame slot count —
   * this knob exists to preview layout density (e.g. how the retake grid
   * handles 6 or 8 photos) without editing any frame.
   */
  photoCount?: number
  /**
   * Override the placeholder strip's aspect (result pages). Values are the
   * print canvas dims (e.g. 1200×1800 = 4R, 600×1800 = 2R strip) — this is the
   * knob for "does the result page contain a strip of THIS shape or overflow".
   */
  stripAspect?: { width: number; height: number }
}

/** Catalog-shaping knobs — vary what the project's catalog LOOKS like to pages. */
export interface CatalogShape {
  /** Target filter count: catalog is cycled/cloned (or sliced) to exactly N. */
  filterCount?: number
  /** Target frame count: catalog is cycled/cloned (or sliced) to exactly N. */
  frameCount?: number
  /**
   * Put a SYNTHETIC frame with this print-canvas size first in the catalog.
   * The first frame drives the scenario's photo ratio and strip placeholder,
   * so this simulates a project whose primary frame is 4R / 2R strip /
   * newspaper / panoramic etc. Real frames stay after it (varied-size project).
   */
  frameAspect?: { width: number; height: number }
}

// ---------------------------------------------------------------------------
// Placeholder media (canvas → data URL)
// ---------------------------------------------------------------------------

const mediaCache = new Map<string, string>()

function makePlaceholderPhoto(width: number, height: number, label: string, hue: number): string {
  const key = `photo|${width}x${height}|${label}|${hue}`
  const cached = mediaCache.get(key)
  if (cached) return cached

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(2, Math.round(width))
  canvas.height = Math.max(2, Math.round(height))
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
  grad.addColorStop(0, `hsl(${hue}, 45%, 72%)`)
  grad.addColorStop(1, `hsl(${(hue + 40) % 360}, 45%, 48%)`)
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // A face-ish circle so crops/masks are visually judgeable.
  ctx.fillStyle = 'rgba(255,255,255,0.35)'
  ctx.beginPath()
  ctx.arc(canvas.width / 2, canvas.height * 0.42, Math.min(canvas.width, canvas.height) * 0.22, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.ellipse(
    canvas.width / 2,
    canvas.height * 0.95,
    Math.min(canvas.width, canvas.height) * 0.38,
    Math.min(canvas.width, canvas.height) * 0.35,
    0,
    Math.PI,
    0
  )
  ctx.fill()

  ctx.fillStyle = 'rgba(0,0,0,0.55)'
  ctx.font = `bold ${Math.round(Math.min(canvas.width, canvas.height) * 0.12)}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, canvas.width / 2, canvas.height / 2)

  const url = canvas.toDataURL('image/jpeg', 0.85)
  mediaCache.set(key, url)
  return url
}

/** Fake-QR placeholder for payment pages (random-looking but deterministic grid). */
function makePlaceholderQr(size = 480): string {
  const key = `qr|${size}`
  const cached = mediaCache.get(key)
  if (cached) return cached

  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, size, size)
  const cells = 29
  const cell = size / (cells + 4)
  ctx.fillStyle = '#111111'
  let seed = 42
  const rand = (): number => {
    seed = (seed * 1103515245 + 12345) % 2147483648
    return seed / 2147483648
  }
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      if (rand() > 0.52) ctx.fillRect((x + 2) * cell, (y + 2) * cell, cell, cell)
    }
  }
  // Finder squares
  const finder = (fx: number, fy: number): void => {
    ctx.fillStyle = '#111111'
    ctx.fillRect(fx, fy, cell * 7, cell * 7)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(fx + cell, fy + cell, cell * 5, cell * 5)
    ctx.fillStyle = '#111111'
    ctx.fillRect(fx + cell * 2, fy + cell * 2, cell * 3, cell * 3)
  }
  finder(2 * cell, 2 * cell)
  finder((cells - 5) * cell, 2 * cell)
  finder(2 * cell, (cells - 5) * cell)
  // Center label so nobody scans it expecting a real payment.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(size * 0.3, size * 0.44, size * 0.4, size * 0.12)
  ctx.fillStyle = '#c0392b'
  ctx.font = `bold ${Math.round(size * 0.07)}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('SIM', size / 2, size / 2)

  const url = canvas.toDataURL('image/png')
  mediaCache.set(key, url)
  return url
}

// ---------------------------------------------------------------------------
// Frame introspection
// ---------------------------------------------------------------------------

/** Unique photo slot types (photo-1..photo-N) of the frame, in slot order. */
function getFrameSlots(frame: FrameOption | undefined): string[] {
  const types = new Set<string>()
  frame?.drawParams?.images?.forEach((img) => {
    if (img.type?.startsWith('photo-')) types.add(img.type)
  })
  const sorted = Array.from(types).sort((a, b) => {
    const na = parseInt(a.replace('photo-', ''), 10)
    const nb = parseInt(b.replace('photo-', ''), 10)
    return (Number.isNaN(na) ? 0 : na) - (Number.isNaN(nb) ? 0 : nb)
  })
  return sorted.length > 0 ? sorted : ['photo-1', 'photo-2', 'photo-3', 'photo-4']
}

function getFramePhotoRatio(frame: FrameOption | undefined): number {
  const photo = frame?.drawParams?.images?.find((img) => img.type?.startsWith('photo-'))
  if (photo && photo.width > 0 && photo.height > 0) return photo.width / photo.height
  return 4 / 3
}

function makeStripPlaceholder(
  frame: FrameOption | undefined,
  override?: { width: number; height: number }
): string {
  const w = override?.width || frame?.drawParams?.canvasWidth || 1200
  const h = override?.height || frame?.drawParams?.canvasHeight || 1800
  // Scale down large canvases; the placeholder only needs to look right.
  const scale = Math.min(1, 900 / Math.max(w, h))
  return makePlaceholderPhoto(w * scale, h * scale, 'STRIP', 210)
}

// ---------------------------------------------------------------------------
// Catalog shaping (filters / frames density + frame aspect fixtures)
// ---------------------------------------------------------------------------

/**
 * Synthetic frame with a given print-canvas size. Slot layout follows the shape:
 * narrow strips get a 4-stacked column (classic 2R photostrip), everything else
 * a 2×2 grid. Thumbnail is a labeled placeholder at the same aspect, so the
 * Frame page's thumbnail grid shows the true shape.
 */
export function makeSyntheticFrame(width: number, height: number): FrameOption {
  const ratio = width / height
  const isStrip = ratio < 0.45
  const margin = 0.06
  const gutter = 0.02

  const images: FrameOption['drawParams']['images'] = []
  if (isStrip) {
    const slotW = 1 - margin * 2
    const slotH = (1 - margin * 2 - gutter * 3) / 4
    for (let i = 0; i < 4; i++) {
      images.push({
        type: `photo-${i + 1}`,
        left: margin * width,
        top: (margin + i * (slotH + gutter)) * height,
        width: slotW * width,
        height: slotH * height
      })
    }
  } else {
    const slotW = (1 - margin * 2 - gutter) / 2
    const slotH = (1 - margin * 2 - gutter) / 2
    for (let i = 0; i < 4; i++) {
      const col = i % 2
      const row = Math.floor(i / 2)
      images.push({
        type: `photo-${i + 1}`,
        left: (margin + col * (slotW + gutter)) * width,
        top: (margin + row * (slotH + gutter)) * height,
        width: slotW * width,
        height: slotH * height
      })
    }
  }

  const thumbScale = Math.min(1, 480 / Math.max(width, height))
  return {
    _id: `sim-frame-${width}x${height}`,
    name: `Sim ${width}×${height}`,
    isActive: true,
    isPublic: true,
    owner: 'sim',
    tags: [],
    thumbnail: makePlaceholderPhoto(width * thumbScale, height * thumbScale, `${width}×${height}`, 30),
    drawParams: {
      canvasWidth: width,
      canvasHeight: height,
      backgroundColor: '#ffffff',
      images
    }
  } as FrameOption
}

/** Cycle `items` into exactly `count` entries; clones get unique ids/names. */
function cycleTo<T extends { _id: string; name: string }>(items: T[], count: number): T[] {
  if (items.length === 0 || count <= 0) return items.slice(0, Math.max(0, count))
  const out: T[] = []
  for (let i = 0; i < count; i++) {
    const src = items[i % items.length]
    if (i < items.length) {
      out.push(src)
    } else {
      out.push({
        ...src,
        _id: `${src._id}~sim${i}`,
        name: `${src.name} ${Math.floor(i / items.length) + 1}`
      })
    }
  }
  return out
}

/**
 * Apply catalog shaping to the pristine catalog. Pure — pass the untouched
 * arrays every time; empty shape returns them unchanged.
 */
export function shapeCatalog(
  pristineFilters: Filter[],
  pristineFrames: FrameOption[],
  shape: CatalogShape
): { filters: Filter[]; frames: FrameOption[] } {
  let frames = pristineFrames
  if (shape.frameAspect) {
    frames = [makeSyntheticFrame(shape.frameAspect.width, shape.frameAspect.height), ...frames]
  }
  if (shape.frameCount !== undefined) {
    frames = cycleTo(frames, Math.max(0, Math.min(48, shape.frameCount)))
  }

  let filters = pristineFilters
  if (shape.filterCount !== undefined) {
    filters = cycleTo(filters, Math.max(0, Math.min(48, shape.filterCount))).map((f, i) =>
      // Cloned LUT filters would try to fetch LUT data by their fake id — make
      // clones manual-mode (originals untouched, they come first in the cycle).
      i >= pristineFilters.length && f.filterMode === 'lut'
        ? { ...f, filterMode: 'manual' as const, lutData: undefined }
        : f
    )
  }

  return { filters, frames }
}

// ---------------------------------------------------------------------------
// Scenario builders
// ---------------------------------------------------------------------------

export const SCENARIO_NAMES = [
  'idle',
  'started',
  'payment-pending',
  'payment-expiring',
  'payment-expired',
  'paid',
  'captured',
  'selected',
  'result',
  'result-live',
  'result-printed',
  'ai-review'
] as const

export type ScenarioName = (typeof SCENARIO_NAMES)[number]

/** Sensible default scenario per page so `?page=result` alone renders something real. */
export const DEFAULT_SCENARIO_BY_PAGE: Record<string, ScenarioName> = {
  welcome: 'idle',
  'mode-select': 'idle',
  tutorial: 'started',
  camera: 'started',
  checkout: 'started',
  payment: 'payment-pending',
  filter: 'paid',
  frame: 'paid',
  capture: 'paid',
  retake: 'captured',
  select: 'captured',
  edit: 'selected',
  processing: 'captured',
  result: 'result',
  'post-session-ai': 'result'
}

function pickPackages(config: ConfigState): Package[] {
  const configured = config.pages?.checkout?.settings?.packages
  const active = (configured || []).filter((p) => p.isActive !== false)
  if (active.length > 0) return [active[0]]
  return [
    {
      type: 'print',
      quantity: 1,
      price: 20000,
      optional: false,
      description: 'Print (sim placeholder)'
    }
  ]
}

/**
 * Build a COMPLETE synthetic SessionStateData for a scenario. The result fully
 * replaces the store's data fields (deterministic — no leftovers from a
 * previous scenario or a real session).
 */
export function buildScenarioState(
  scenario: ScenarioName,
  ctx: ScenarioContext,
  opts: ScenarioOptions = {}
): SessionStateData {
  const now = Date.now()
  const frame = ctx.frames[0]
  const filter = ctx.filters[0]
  const frameSlots = getFrameSlots(frame)
  const photoCount =
    opts.photoCount && Number.isFinite(opts.photoCount)
      ? Math.max(1, Math.min(24, Math.round(opts.photoCount)))
      : frameSlots.length
  // When overriding beyond the frame's real slots, synthesize photo-N types so
  // slot-keyed pages (PrintPreview etc.) still get stable, unique mappings.
  const slots =
    photoCount === frameSlots.length
      ? frameSlots
      : Array.from({ length: photoCount }, (_, i) => frameSlots[i] ?? `photo-${i + 1}`)
  const ratio = getFramePhotoRatio(frame)
  const photoW = 900
  const photoH = Math.round(photoW / ratio)

  const sessionId = `sim-${now}`
  const captureSettings = ctx.config.pages?.capture?.settings

  const base: SessionStateData = {
    id: sessionId,
    projectId: ctx.projectId,
    userId: ctx.userId,
    eventId: undefined,
    userEmail: undefined,
    mode: 'capture',
    timeline: {
      appLaunchTime: new Date(now - 3_600_000),
      startTime: new Date(now - 90_000),
      endTime: null,
      duration: null,
      isCompleted: false
    },
    userPreferences: {
      filterId: filter?._id ?? null,
      frameId: frame?._id ?? null,
      copies: 1,
      retakeCount: 0,
      retakeSessionsCompleted: 0,
      framePhotoRatio: ratio,
      useGIF: captureSettings?.gifEnabled ?? true,
      useRecording: captureSettings?.recordingEnabled ?? true,
      useVideo: true,
      useDigital: true,
      usePrint: true,
      usePax: false,
      selectedCamera: 'primary',
      email: '',
      phoneNumber: '',
      satisfactionRating: null,
      satisfactionTimestamp: null
    },
    userConsent: {
      photoPublication: false,
      consentTimestamp: new Date(now)
    },
    devices: {
      camera: { id: 'sim-camera', name: 'Sim Camera', status: 'ready', type: 'webcam' },
      printer: { id: 'sim-printer', name: 'Sim Printer', status: 'ready' }
    },
    transaction: {
      orderId: null,
      amount: 0,
      discount: 0,
      qrUrl: null,
      expiryTime: null,
      serverTime: null,
      status: 'pending',
      extraPrintPaymentStatus: undefined,
      selectedPackages: [],
      printed: false,
      extraPrintAmount: 0,
      extraPrintCopies: 0
    },
    gallery: {
      id: `${9999999999999 - now}-${sessionId}`,
      stripUrl: null,
      capturedItems: [],
      filterCache: {}
    },
    galleryKey: `${9999999999999 - now}-${sessionId}`,
    logs: []
  }

  if (scenario === 'idle') {
    // No session: welcome screens and the PageWrapper "no session" behaviour.
    return { ...base, id: '', timeline: { ...base.timeline, startTime: null } }
  }

  if (scenario === 'started') {
    return base
  }

  const packages = pickPackages(ctx.config)
  const amount = packages.reduce((sum, p) => sum + p.price * Math.max(1, p.quantity), 0)

  const withPayment = (
    status: SessionStateData['transaction']['status'],
    expiresInSec: number
  ): SessionStateData => ({
    ...base,
    transaction: {
      ...base.transaction,
      orderId: `SIM-${now}`,
      amount,
      selectedPackages: packages,
      qrUrl: makePlaceholderQr(),
      serverTime: new Date(now).toISOString(),
      expiryTime: new Date(now + expiresInSec * 1000).toISOString(),
      status
    }
  })

  switch (scenario) {
    case 'payment-pending':
      return withPayment('pending', opts.expiresIn ?? 300)
    case 'payment-expiring':
      return withPayment('pending', opts.expiresIn ?? 10)
    case 'payment-expired':
      return withPayment('expire', -(opts.expiresIn ?? 60))
    default:
      break
  }

  // Everything below is post-payment.
  const paid = withPayment('settlement', 900)

  if (scenario === 'paid') return paid

  const capturedItems = slots.map((slotType, i) => ({
    id: `sim-capture-${i + 1}`,
    photoUrl: makePlaceholderPhoto(photoW, photoH, `PHOTO ${i + 1}`, (i * 47) % 360),
    photoType: slotType,
    captureIndex: i,
    timestamp: new Date(now - (slots.length - i) * 5000)
  }))

  const captured: SessionStateData = {
    ...paid,
    gallery: { ...paid.gallery, capturedItems }
  }

  if (scenario === 'captured') return captured

  const selected: SessionStateData = {
    ...captured,
    userPreferences: { ...captured.userPreferences, filterId: filter?._id ?? null, copies: 1 }
  }

  if (scenario === 'selected') return selected

  const stripUrl = makeStripPlaceholder(frame, opts.stripAspect)
  const result: SessionStateData = {
    ...selected,
    timeline: { ...selected.timeline, isCompleted: true },
    gallery: {
      ...selected.gallery,
      stripUrl,
      stripGenerationStatus: 'generated',
      capturedItems: [
        ...selected.gallery.capturedItems,
        {
          id: 'sim-strip',
          photoUrl: stripUrl,
          stripUrl,
          timestamp: new Date(now)
        }
      ]
    }
  }

  if (scenario === 'result') return result

  if (scenario === 'result-live') {
    return {
      ...result,
      userPreferences: { ...result.userPreferences, recordingType: 'strip_live' },
      gallery: { ...result.gallery, liveModeProcessing: true, liveModeUrl: null }
    }
  }

  if (scenario === 'result-printed') {
    return {
      ...result,
      transaction: { ...result.transaction, printed: true },
      timeline: {
        ...result.timeline,
        endTime: new Date(now),
        duration: 90_000,
        isCompleted: true
      }
    }
  }

  if (scenario === 'ai-review') {
    // Post-session AI, REVIEW stage: every captured raw photo already has an
    // AI-processed result, so PostSessionAI lands on the compare/continue view
    // directly (showContinue = activeItem.aiProcessedPhotoUrl) without a live
    // generation. The "after" placeholder is a distinct hue so before≠after is
    // visually obvious in the comparison slider.
    return {
      ...result,
      gallery: {
        ...result.gallery,
        capturedItems: result.gallery.capturedItems.map((it, i) =>
          it.photoType?.startsWith('photo-')
            ? {
                ...it,
                aiProcessedPhotoUrl: makePlaceholderPhoto(photoW, photoH, `AI ${i + 1}`, (i * 47 + 180) % 360)
              }
            : it
        )
      }
    }
  }

  return base
}
